





















































































































import { Component, Vue } from 'vue-property-decorator';
import { mdbBtn, mdbIcon, mdbTooltip, mdbAlert, mdbProgress } from '@folky-tools/mdbvue';
import { usersService, axiosService, authorizationManagerService, userInvitationsService, notificationsService } from '@/services';
import { UserActionLogInfo } from '@/models/users/user-logging/user-action-log-info';
import { UserActionType } from '@/models/users/user-logging/user-action-type';
import { UserActionEntityType } from '@/models/users/user-logging/user-action-entity-type';
import { UserActionScopeType } from '@/models/users/user-logging/user-action-scope-type';
import { Invitation } from '@/models/users/user-invitations/invitation';
import { VueGoodTable } from '@folky-tools/vue-good-table';

@Component({
    components: {
        mdbBtn,
        mdbIcon,
        mdbTooltip,
        mdbAlert,
        mdbProgress,
        VueGoodTable,
        AppSysBar: () => import('@/components/shared/AppSysBar.vue')
    }
})
export default class UserInvitationsListView extends Vue {
    private userInvitationsPerPage: number = 20;
    private actualUserCanCreateUserInvitations: boolean = false;
    private rows: Invitation[] = [];
    private columns: any[] = [];
    private totalRecords: number = 0;
    private isLoading: boolean = false;
    private serverParams: any = {
        columnFilters: {
            tradeName: ''
        },
        sort: [
            {
                field: 'id',
                type: 'desc'
            }
        ],
        page: 1,
        perPage: this.userInvitationsPerPage
    };

    private async beforeMount(): Promise<void> {
        await this.setColumns();
        await this.loadItems();
    }

    private async mounted(): Promise<void> {
        this.actualUserCanCreateUserInvitations = authorizationManagerService.canCreateUserInvitations;
        this.$root.$emit('change-active-page', 'views.users.userInvitationsListView.title');

        await usersService.logUserAction(
            new UserActionLogInfo({
                type: UserActionType.HasVisited,
                entity: UserActionEntityType.UserInvitations,
                scope: UserActionScopeType.List
            })
        );
    }

    private async resendInvitation(id: number): Promise<void> {
        if (await userInvitationsService.resendInvitation(id)) {
            this.loadItems();

            notificationsService.showToastSuccessNotification('views.users.userInvitationsListView.actions.resend.successMessage');

            await usersService.logUserAction(
                new UserActionLogInfo({
                    type: UserActionType.HasChanged,
                    entity: UserActionEntityType.UserInvitations,
                    scope: UserActionScopeType.Resend,
                    entityId: id
                })
            );

            return;
        }

        notificationsService.showToastErrorNotification();
    }

    private updateParams(newProps: any): void {
        this.serverParams = { ...this.serverParams, ...newProps };
    }

    private async onPageChange(params: any): Promise<void> {
        this.updateParams({ page: params.currentPage });
        await this.loadItems();
    }

    private async onSortChange(params: any): Promise<void> {
        this.updateParams({
            sort: [
                {
                    type: params[0].type,
                    field: params[0].field
                }
            ]
        });
        await this.loadItems();
    }

    private async onColumnFilter(params: any): Promise<void> {
        this.serverParams.page = 1;
        this.updateParams(params);
        await this.loadItems();
    }

    private async onPerPageChange(params: any): Promise<void> {
        this.updateParams({ perPage: params.currentPerPage });
        await this.loadItems();
    }

    private async loadItems(): Promise<void> {
        this.isLoading = true;

        const response: any = await axiosService.instance.get('users/organization/invitations/query', {
            params: this.getQueryParams(this.serverParams.sort, this.serverParams.page, this.serverParams.perPage)
        });

        this.rows = [];

        this.totalRecords = response.data.count;
        response.data.items.forEach((item: any) => {
            return this.rows.push(new Invitation(item));
        });

        this.isLoading = false;
    }

    private getQueryParams(sortOrder: any, currentPage: number, perPage: number): any {
        return {
            $skip: (currentPage - 1) * perPage,
            $top: perPage,
            $orderby: this.getQueryOrderFromColumn(sortOrder),
            $filter: this.getQueryFilterFromColumns()
        };
    }

    private getQueryOrderFromColumn(sortOrder: any): string | null {
        if (sortOrder && sortOrder.length === 1) {
            if (sortOrder[0].field === 'lastSendedUtc') {
                return `updatedUtc ${sortOrder[0].type}`;
            } else {
                return `${sortOrder[0].field} ${sortOrder[0].type}`;
            }
        } else {
            return null;
        }
    }

    private getQueryFilterFromColumns(): string | null {
        let queryFilter = '';
        const logicalOperatorBetweenFilters = ' and ';

        for (const key of Object.keys(this.serverParams.columnFilters)) {
            if (this.serverParams.columnFilters[key]) {
                if (key === 'email') {
                    queryFilter += `${logicalOperatorBetweenFilters}contains(email, '${this.serverParams.columnFilters[key].replace('\'', '\'\'')}')`;
                }
            }
        }

        return queryFilter === '' ? null : queryFilter.substring(logicalOperatorBetweenFilters.length);
    }

    private setColumns(): void {
        this.columns = [
            { field: 'id', type: 'number', sortable: true },
            { field: 'accepted', type: 'text' },
            {
                field: 'email',
                type: 'text',
                filterOptions: {
                    enabled: true,
                    placeholder: this.$t('common.placeholders.searchByEmail'),
                    filterValue: ''
                }
            },
            { field: 'originApplicationName', type: 'text' },
            { field: 'destinationApplicationName', type: 'text' },
            { field: 'count', type: 'text' },
            { field: 'lastSendedUtc', type: 'text' }
        ];
    }
}
